import { useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import Page from "@/components/layout/Page";
import { ProductModal } from "@/components/ProductModal";
import { ProductQuickView } from "@/components/ProductQuickView";
import { ProductImagePlaceholder } from "@/components/ProductImagePlaceholder";
import { products, type Product } from "@/data/products";
import { SEOHead } from "@/components/seo/SEOHead";
import { BreadcrumbSchema } from "@/components/seo/StructuredData";

export default function Shop() {
  const { addItem } = useCart();
  const { toast } = useToast();
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  // NOTE: Shop products are sorted by price (high to low) to prioritise B2B and kit purchases.
  const sortedProducts = [...products].sort((a, b) => b.price - a.price);

  const handleAddToCart = (product: Product) => {
    addItem({
      id: product.slug.replace("/", ""),
      name: product.name,
      slug: product.slug,
      price: product.price,
    });
    toast({
      title: "Added to cart",
      description: `${product.name} has been added to your cart.`,
    });
  };

  const handleViewDetails = (product: Product) => {
    setSelectedProduct(product);
  };
  
  return (
    <>
      <SEOHead
        title="Shop Sensory Tools"
        description="Shop premium tactile sensory boards, Library Squares, and Sensory Pads. Handmade in the UK for ADHD, autism, anxiety, and focus support."
        canonical="/shop"
        keywords="buy sensory boards, sensory tools shop, ADHD tools, autism sensory products, tactile learning tools, focus tools for office"
      />
      <BreadcrumbSchema items={[
        { name: "Home", url: "/" },
        { name: "Shop", url: "/shop" }
      ]} />
      
      <Page>
      <section className="min-h-screen w-full">
        <div className="pb-24 flex flex-col gap-8">
        <header className="space-y-3 text-center md:text-left">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-emerald-300/80">
            Shop
          </p>
          <h1 className="text-3xl font-semibold tracking-tight page-title-gradient md:text-4xl">
            Choose the right Samboards tools for your space
          </h1>
          <p className="max-w-2xl text-sm text-slate-300/80">
            From full-size Samboards to portable Mini boards and specialist
            ranges, every product is designed to support autistic, ADHD and
            neurodivergent people in homes, schools and clinical settings.
          </p>
        </header>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {sortedProducts.map((product) => (
            <article
              key={product.slug}
              className="group relative flex flex-col h-full rounded-3xl border border-white/5 bg-slate-900/70 shadow-[0_18px_60px_rgba(0,0,0,0.65)] backdrop-blur-xl interactive-card"
            >
              {/* Product Badge */}
              {product.badge && (
                <span className="product-badge">
                  {product.badge}
                </span>
              )}
              {product.outOfStock && (
                <span className="absolute top-3 left-3 z-20 inline-flex items-center rounded-full border border-slate-400/50 bg-slate-500/20 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.15em] text-slate-300 backdrop-blur-sm">
                  Out of stock
                </span>
              )}
              
              {/* Quick View Tooltip */}
              {product.features && product.features.length > 0 && (
                <div className="absolute top-3 right-3 z-10 pointer-events-none">
                  <ProductQuickView
                    features={product.features}
                    productName={product.name}
                  />
                </div>
              )}
              
              <div className="w-full h-[220px] md:h-[260px] lg:h-[300px] overflow-hidden rounded-t-3xl product-card-image-container">
                {product.image && product.image.src !== "/images/placeholder.png" ? (
                  <div className="w-full h-full bg-gradient-to-br from-slate-800/60 to-slate-900/80 p-6">
                    <img 
                      src={product.image.src} 
                      alt={product.image.alt}
                      className="w-full h-full object-contain transition-transform duration-300"
                      style={{ 
                        imageRendering: '-webkit-optimize-contrast',
                        backfaceVisibility: 'hidden',
                        transform: 'translateZ(0)'
                      }}
                    />
                  </div>
                ) : (
                  <ProductImagePlaceholder className="w-full h-full" aspectRatio="" />
                )}
              </div>
              
              <div className="flex flex-col flex-1 px-5 py-6">
                <div className="space-y-3 flex-1">
                  <h2 className="text-xl font-semibold text-emerald-50 leading-tight">
                    {product.name}
                  </h2>
                  <p className={`text-sm leading-relaxed ${product.description === "Description coming soon" ? "text-slate-400/60 italic" : "text-slate-200/80"}`}>
                    {product.description}
                  </p>
                </div>

                <div className="mt-6 flex flex-col gap-3">
                  {product.price > 0 && (
                    <div className="flex items-baseline">
                      <span className="text-2xl font-bold text-emerald-300">
                        £{product.price.toFixed(2)}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex flex-col xs:flex-row gap-2">
                    <Button
                      onClick={() => handleAddToCart(product)}
                      className="w-full rounded-full bg-gradient-to-r from-emerald-300 to-cyan-300 text-slate-900 font-medium shadow-[0_0_40px_rgba(45,255,196,0.35)] interactive-button disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none py-3 text-base"
                      disabled={product.outOfStock}
                    >
                      {product.outOfStock ? 'Out of stock' : 'Add to cart'}
                    </Button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleViewDetails(product);
                      }}
                      className="w-full rounded-full border-2 border-emerald-300/50 bg-transparent text-emerald-300 hover:bg-emerald-300/10 hover:text-emerald-200 hover:border-emerald-300 interactive-glow px-4 py-3 text-base font-semibold transition-all duration-200 cursor-pointer"
                    >
                      View details
                    </button>
                  </div>
                </div>
              </div>
            </article>
          ))}

          {/* Access to Work CTA Card */}
          <article className="group relative flex flex-col h-full rounded-3xl border border-emerald-300/30 bg-gradient-to-br from-emerald-900/20 to-slate-900/70 shadow-[0_18px_60px_rgba(0,0,0,0.65)] backdrop-blur-xl interactive-card">
            <div className="flex flex-col flex-1 px-5 py-6">
              <div className="space-y-3 flex-1">
                <span className="inline-flex items-center rounded-full border border-emerald-300/50 bg-emerald-300/10 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.15em] text-emerald-200">
                  Workplace Support
                </span>
                <h2 className="text-xl font-semibold text-emerald-50 leading-tight">
                  Access to Work Kits
                </h2>
                <p className="text-sm leading-relaxed text-slate-200/80">
                  Complete sensory support packages eligible for Access to Work funding. Perfect for neurodivergent employees seeking workplace adjustments.
                </p>
              </div>

              <div className="mt-6">
                <Link
                  to="/access-to-work-kits"
                  className="inline-flex w-full items-center justify-center rounded-full bg-gradient-to-r from-emerald-300 to-cyan-300 px-4 py-2.5 text-sm font-medium text-slate-900 shadow-[0_0_40px_rgba(45,255,196,0.35)] interactive-button"
                >
                  Learn more
                </Link>
              </div>
            </div>
          </article>
        </div>
        </div>
      </section>

      <ProductModal
        isOpen={selectedProduct !== null}
        onClose={() => setSelectedProduct(null)}
        product={selectedProduct || {
          name: "",
          price: 0,
          description: ""
        }}
        onAddToCart={() => selectedProduct && handleAddToCart(selectedProduct)}
      />
    </Page>
    </>
  );
}
